import { Injectable } from '@angular/core';
import { AbsencePlanerBackendService } from '../../services/absence-planer/absence-planer-backend.service';
import { AbsencePlanerStore } from './absence-planer.store';
import { SessionQuery } from '../session/session.query';
import { CalendarItemBackendService } from '../../services/calendar-item/calendar-item-backend.service';
import { DateRange } from '../../models/date-range';
import { AbsencePlanerQuery } from './absence-planer.query';
import { applyTransaction } from '@datorama/akita';
import { TimeFrameModel } from '../../models/absence-planer/time-frame.model';
import { AbsenceBackendService } from '../../services/absence/absence-backend.service';
import { ToastService } from '../../services/toast-service';
import { VisumBackendService } from '../../services/visum/visum-backend.service';
import { RemoveVisumService } from '../../interfaces/absence/remove-visum.service';
import { forkJoin, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { TextKeys } from '../../i18n/text-keys';
import { VisumService } from '../../services/visum/visum.service';
import {
  AbsenceAutoTimeEntry,
  AbsenceAutoTimeEntryAdmin,
  AbsenceConfirmationItem,
  AbsencePlanerUserList,
  AbsencePlanerUserParameter,
  ChronoUserPersonDateRange,
  HolidayOverdraftCheckResponse,
  PersonDateRange,
} from '../../models/client-models';
import { SaldoBackendService } from '../../services/saldo/saldo-backend.service';
import { SaldoRequestModel } from '../../models/saldo/saldo-request.model';

@Injectable({
  providedIn: 'root',
})
export class AbsencePlanerService implements RemoveVisumService {
  constructor(
    private backendService: AbsencePlanerBackendService,
    private calendarService: CalendarItemBackendService,
    private translateService: TranslateService,
    private store: AbsencePlanerStore,
    private query: AbsencePlanerQuery,
    private sessionQuery: SessionQuery,
    private toastService: ToastService,
    private visumBackendService: VisumBackendService,
    private absenceService: AbsenceBackendService,
    private visumService: VisumService,
    private saldoBackendService: SaldoBackendService
  ) {}

  private toIsoFromDmy(value: string | Date): string {
    if (value instanceof Date) return value.toISOString();
    const s = (value ?? '').toString().trim();

    if (/^\d{4}-\d{2}-\d{2}/.test(s)) return new Date(s).toISOString();

    const m = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(s);
    if (m) {
      const [, dd, mm, yyyy] = m;
      return new Date(Number(yyyy), Number(mm) - 1, Number(dd)).toISOString();
    }

    return new Date(s).toISOString();
  }

  private formatAccountValue(acc: any): { label: string; positive: boolean } {
    const unitHours = this.translateService.instant(
      TextKeys.badge.saldoUnitHours
    );
    const unitDays = this.translateService.instant(TextKeys.badge.saldoUnitDay);

    const fmt = (n: number | null | undefined) =>
      n == null ? '-' : (Math.round(Number(n) * 100) / 100).toFixed(2);

    const parts: string[] = [];

    if (acc?.saldoVisible) {
      parts.push(`${fmt(acc.saldo)} ${unitHours}`);
    }

    if (acc?.saldoInDaysVisible) {
      parts.push(`${fmt(acc.saldoInDays)} ${unitDays}`);
    }

    if (!parts.length && acc?.saldo != null) {
      parts.push(`${fmt(acc.saldo)} ${unitHours}`);
    }

    const label = parts.join(' / ') || '-';

    const signBase = acc?.saldoVisible
      ? Number(acc?.saldo ?? 0)
      : acc?.saldoInDaysVisible
        ? Number(acc?.saldoInDays ?? 0)
        : Number(acc?.saldo ?? 0);

    return { label, positive: signBase >= 0 };
  }

  private saldoTodayCache = new Map<number, any>();

  private invalidateSaldoTodayCacheForPerson(idPerson: number) {
    if (!idPerson) return;
    this.saldoTodayCache.delete(idPerson);
  }

  private getSaldoToday$(idPerson: number) {
    if (!idPerson) return of(undefined);

    const cached = this.saldoTodayCache.get(idPerson);
    if (cached) return cached;

    const req: SaldoRequestModel = {
      IDPerson: idPerson,
      Date: new Date(),
    };

    const obs$ = this.saldoBackendService.getSaldo(req).pipe(
      shareReplay(1),
      catchError(() => of(undefined))
    );

    this.saldoTodayCache.set(idPerson, obs$);
    return obs$;
  }

  private getRequestedHoursForRow(
    row: AbsenceConfirmationItem
  ): number | undefined {
    if (!row.absences?.length) return undefined;

    const totalZzm = row.absences.reduce(
      (sum, a) => sum + Number(a.zzm ?? 0),
      0
    );
    if (totalZzm <= 0) return undefined;

    // Nur bei manuellen Absenzen mitschicken (Korrektur == 3)
    const isManual = row.absences.every(
      (a) => Number((a as any).korrektur ?? 0) === 3
    );
    if (!isManual) return undefined;

    return totalZzm;
  }

  public buildOverdraftTable$(rows: AbsenceConfirmationItem[]) {
    if (!rows?.length) return of<AbsenceConfirmationItem[]>([]);

    const perRow$ = rows.map((row) => {
      const check$ = this.absenceService
        .checkOverdraft({
          idPerson: row.idPerson,
          startDate: this.toIsoFromDmy(row.from),
          endDate: this.toIsoFromDmy(row.to),
          absenceId: row.idAbsence ?? 0,
          requestedHours: this.getRequestedHoursForRow(row),
        })
        .pipe(
          map(
            (res) => (res as any)?.resultObject as HolidayOverdraftCheckResponse
          ),
          catchError(() => of(undefined))
        );

      const saldo$ = this.getSaldoToday$(row.idPerson);

      return forkJoin({ check: check$, saldo: saldo$ }).pipe(
        map(({ check, saldo }) => {
          const accounts: any[] = (saldo as any)?.accounts ?? [];
          const acc9List = accounts.filter((a) => a?.konto === '9');

          const dec31Acc = acc9List.length
            ? acc9List[acc9List.length - 1]
            : null;

          const { label: prognoseLabel, positive } = dec31Acc
            ? this.formatAccountValue(dec31Acc)
            : { label: '-', positive: undefined };

          const angefragtLabel = (() => {
            if (!check) return row.angefragt ?? '-';

            const unitHours = this.translateService.instant(
              TextKeys.badge.saldoUnitHours
            );
            const unitDays = this.translateService.instant(
              TextKeys.badge.saldoUnitDay
            );
            const fmt = (n: number | null | undefined) =>
              n == null ? '-' : (Math.round(Number(n) * 100) / 100).toFixed(2);

            const parts: string[] = [];
            if (check.requestedHolidayHours != null)
              parts.push(`${fmt(check.requestedHolidayHours)} ${unitHours}`);
            if (check.requestedHolidayDays != null)
              parts.push(`${fmt(check.requestedHolidayDays)} ${unitDays}`);

            return parts.join(' / ') || '-';
          })();

          return {
            ...row,
            angefragt: angefragtLabel,
            prognose: prognoseLabel,
            prognosePositive: positive,

            overdraft: {
              currentHours: 0,
              requestedHours: Number(check?.requestedHolidayHours ?? 0),
              projectedHours: Number(dec31Acc?.saldo ?? 0),

              currentLabel: '-',
              requestedLabel: angefragtLabel,
              projectedLabel: prognoseLabel,

              positive: !!positive,
            },
          } as AbsenceConfirmationItem;
        }),
        catchError(() => of(row))
      );
    });

    return forkJoin(perRow$).pipe(map((list) => [...list]));
  }

  toggleRequestAll() {
    this.store.update((state) => ({
      ...state,
      allRequests: !state.allRequests,
    }));
  }

  removeVisum(callback: Function) {
    const personID = this.query.getSelectedResource()?.idPerson;
    const date = this.query.getDateRange()?.start;
    const chronoUserID = this.sessionQuery.getAdmiaUserID();
    const logedInPersonID = this.sessionQuery.getPersonID();

    this.visumService
      .createVisumAlert(
        callback,
        date,
        personID,
        chronoUserID,
        logedInPersonID,
        null,
        TextKeys.timeEntryForm.visumRemoveTitle,
        TextKeys.timeEntryForm.visumQuestion
      )
      .subscribe((alert) => alert.present());

    // if (personID === logedInPersonID) {
    //   return this.visumBackendService.removeVisum(personID, date);
    // } else {
    //   return this.visumBackendService.removeVisumChronoUser(
    //     chronoUserID,
    //     personID,
    //     date
    //   );
    // }
  }

  //NOTE JSON.stringify is low performance issue here
  getUserList(filterID: number) {
    this.store.setLoading(true);

    const person = this.sessionQuery.getValue()?.user;
    const grouping = this.query.getGrouping();
    const filter = this.query.getFilter();

    this.backendService
      .getListOfAbsences({
        chronoUserID: person.user.id,
        personalID: person.id,
        grouping,
        filter,
        filterID,
      } as AbsencePlanerUserParameter)
      .subscribe((value) => {
        applyTransaction(() => {
          const list = value.map((u) => {
            if (u.idPerson === this.sessionQuery.getPersonID()) {
              const changedInfo = JSON.parse(
                JSON.stringify(u)
              ) as AbsencePlanerUserList;
              changedInfo.edit =
                this.sessionQuery.getValue()?.user?.authorization
                  ?.absenceEdit ||
                this.sessionQuery.getValue()?.user?.authorization?.absenceAsk;
              return changedInfo;
            }
            return u;
          });

          this.store.update((state) => ({
            ...state,
            users: list,
          }));
        });
      });
  }

  //NOTE JSON.stringify is low performance issue here
  getRequestedAbsences() {
    const admiaUser = this.sessionQuery.getAdmiaUserID() ?? 0;
    const allAbsences = this.query.getAllRequests();

    if (admiaUser !== 0) {
      this.backendService
        .getListOfAbsencesToConfirm(admiaUser, allAbsences)
        .subscribe((value) => {
          if (
            JSON.stringify(this.query.getValue()?.requestedAbsences) !==
            JSON.stringify(value)
          ) {
            this.store.update((state) => ({
              ...state,
              requestedAbsences: value,
            }));
          }
        });
    }
  }

  getCalendarItems(range: DateRange) {
    this.store.setLoading(true);

    const users = this.query.getValue()?.users;
    this.calendarService
      .getCalendarItemsPlaner(range, users)
      .pipe(tap(() => this.store.setLoading(false)))
      .subscribe((value) => {
        applyTransaction(() => {
          this.setVisibleDateRange(range);
          this.store.update((state) => ({
            ...state,
            calendarItems: value,
          }));
        });
      });
  }

  setGrouping(grouping: number) {
    applyTransaction(() => {
      this.store.update((state) => ({
        ...state,
        grouping: Number(grouping),
      }));
      this.getUserList(this.query.getFilterID() ?? 0);
    });
  }

  setFilter(filter: number) {
    applyTransaction(() => {
      this.store.update((state) => ({
        ...state,
        filter: Number(filter),
      }));
    });
  }

  setFilterID(filterValue: number) {
    applyTransaction(() => {
      this.store.update((state) => ({
        ...state,
        filterID: Number(filterValue),
      }));

      this.getUserList(Number(filterValue));
    });
  }

  setTimeFrame(timeFrame: TimeFrameModel) {
    this.store.update((state) => ({
      ...state,
      selectedTimeFrame: timeFrame,
    }));
  }

  setSelectedDateRange(dateRange: DateRange, resource: AbsencePlanerUserList) {
    this.store.update((state) => ({
      ...state,
      selectedResource: resource,
      selectedDateRange: dateRange,
    }));
    this.getVisumInfo();
  }

  createAbsence(dateRange: DateRange, idAbsence: number) {
    const resource = this.query.getSelectedResource();
    const chronoUser = this.sessionQuery.getAdmiaUserID();
    const logedInPersonID = this.sessionQuery.getPersonID();
    this.store.setLoading(true);

    if (resource) {
      if (logedInPersonID === resource.idPerson) {
        this.absenceService
          .createAbsences({
            idPerson: resource.idPerson,
            startDate: dateRange.start,
            endDate: dateRange.end,
            id: idAbsence,
          } as AbsenceAutoTimeEntry)
          .subscribe({
            next: (value) => {
              this.getCalendarItems(this.query.getVisibleDateRange());
              this.store.setLoading(false);
              this.toastService.presentInfoToast(
                { message: value.message },
                'tabs-bottom-higher'
              );
            },
            error: (error) => {
              this.store.setLoading(false);
              this.toastService.presentErrorToast(
                { message: error?.error },
                'tabs-bottom-higher'
              );
            },
          });
      } else {
        this.absenceService
          .createAbsencesAsAdmin({
            idPerson: resource.idPerson,
            idChronoUser: chronoUser,
            startDate: dateRange.start,
            endDate: dateRange.end,
            id: idAbsence,
          } as AbsenceAutoTimeEntryAdmin)
          .subscribe({
            next: (value) => {
              this.getCalendarItems(this.query.getVisibleDateRange());
              this.store.setLoading(false);
              this.toastService.presentInfoToast(
                { message: value.message },
                'tabs-bottom-higher'
              );
            },
            error: (error) => {
              this.store.setLoading(false);
              this.toastService.presentErrorToast(
                { message: error?.error },
                'tabs-bottom-higher'
              );
            },
          });
      }
    }
  }

  setVisibleDateRange(dateRange: DateRange) {
    this.store.update((state) => ({
      ...state,
      visibleDateRange: dateRange,
    }));
  }

  getVisumInfo() {
    const personID = this.query.getSelectedResource()?.idPerson;
    const date = this.query.getDateRange()?.start;
    const chronoUserID = this.sessionQuery.getAdmiaUserID();
    const logedInPersonID = this.sessionQuery.getPersonID();

    if (personID === logedInPersonID) {
      this.visumBackendService
        .getVisumInfoForDay(personID, date)
        .subscribe((data) => {
          this.store.update((state) => ({
            ...state,
            selectedDateVisumInfo: data,
          }));
        });
    } else {
      this.visumBackendService
        .getVisumInfoForDayChronoUser(personID, date, chronoUserID)
        .subscribe((data) => {
          this.store.update((state) => ({
            ...state,
            selectedDateVisumInfo: data,
          }));
        });
    }
  }

  deleteAbsence() {
    const personID = this.query.getSelectedResource()?.idPerson;
    const startDate = this.query.getDateRange()?.start;
    const endDate = this.query.getDateRange()?.end;
    const chronoUserID = this.sessionQuery.getAdmiaUserID();
    const logedInPersonID = this.sessionQuery.getPersonID();
    this.store.setLoading(true);

    if (personID === logedInPersonID) {
      const absenceRange = {
        personID,
        startDate,
        endDate,
      } as PersonDateRange;
      this.absenceService
        .deleteAbsence(absenceRange)
        .pipe(
          tap(() => this.store.setLoading(false)),
          catchError((error) => of(error.error))
        )
        .subscribe(
          (result) => {
            this.getCalendarItems(this.query.getVisibleDateRange());
            this.store.setLoading(false);
            this.toastService.presentInfoToast(
              { message: result.message },
              'tabs-bottom-higher'
            );
          },
          (error) => {
            this.store.setLoading(false);
            this.toastService.presentErrorToast(
              { message: error.message },
              'tabs-bottom-higher'
            );
          }
        );
    } else {
      const absenceRange = {
        personID,
        startDate,
        endDate,
        chrnoUserID: chronoUserID,
      } as ChronoUserPersonDateRange;
      this.absenceService
        .deleteAbsenceAsChronoUser(absenceRange)
        .pipe(
          tap(() => this.store.setLoading(false)),
          catchError((error) => of(error.error))
        )
        .subscribe(
          (result) => {
            this.store.setLoading(false);
            this.getCalendarItems(this.query.getVisibleDateRange());
            this.toastService.presentInfoToast(
              { message: result.message },
              'tabs-bottom-higher'
            );
          },
          (error) => {
            this.store.setLoading(false);
            this.toastService.presentErrorToast(
              { message: error.message },
              'tabs-bottom-higher'
            );
          }
        );
    }
  }

  updateList() {
    this.getCalendarItems(this.query.getVisibleDateRange());
  }

  sendAbsenceDecision(data: AbsenceConfirmationItem) {
    this.store.setLoading(true);

    this.backendService.sendAbsenceRequestDecision(data).subscribe({
      next: (value) => {
        if (value.successful) {
          // ✅ Cache invalidieren für alle betroffenen Personen
          // Fall 1: data enthält direkt eine Person
          if ((data as any).idPerson) {
            this.invalidateSaldoTodayCacheForPerson((data as any).idPerson);
          }

          // Fall 2: data.absences = Liste von Absencen
          if (Array.isArray((data as any).absences)) {
            const uniquePersons = new Set<number>(
              ((data as any).absences as any[])
                .map((a) => a.idPerson as number | undefined)
                .filter((id): id is number => typeof id === 'number' && !!id)
            );

            uniquePersons.forEach((id) =>
              this.invalidateSaldoTodayCacheForPerson(id)
            );
          }

          // ✅ Toasts
          if (!data.accepted) {
            this.toastService.presentInfoToast({
              message: this.translateService.instant(
                TextKeys.absencePlaner.deniedAbsence
              ),
            });
          } else {
            this.toastService.presentInfoToast({
              message: this.translateService.instant(
                TextKeys.absencePlaner.confirmedAbsence
              ),
            });
          }
        } else {
          this.toastService.presentErrorToast({
            message: this.translateService.instant(
              TextKeys.absencePlaner.errorInConfirming
            ),
          });
        }

        // ✅ restliche Refreshes
        this.getUserList(this.query.getFilterID() ?? 0);
        this.getRequestedAbsences();
        this.getCalendarItems(this.query.getDateRange());

        this.store.setLoading(false);
      },
      error: () => {
        this.store.setLoading(false);
        this.toastService.presentErrorToast({
          message: this.translateService.instant(
            TextKeys.absencePlaner.errorInConfirming
          ),
        });
      },
    });
  }
}
